// Hideo - script editor: the Fountain screenplay format (https://fountain.io/syntax), without UI. A Fountain script is
// plain text; parseFountain reads it into a title page and a list of elements (scene headings, action, character cues
// with their parentheticals and dialogue, transitions…), which the Fountain mode of the editor draws as a screenplay
// (src/features/editor/fountainHtml.ts), counts in pages (paginate: about 55 lines a page, 1 page ≈ 1 minute on screen)
// and lists in its scene and character panels (FountainEditor.vue). Tests: tests/fountain.test.ts.
// Lenient on purpose where typing needs it: a scene heading needs an empty line before it, not after it.
import { esc } from '../../utils/html'

/** The kinds of element of a screenplay */
export type FountainKind = 'scene' | 'action' | 'character' | 'parenthetical' | 'dialogue' | 'lyrics' | 'transition'
  | 'centered' | 'section' | 'synopsis' | 'note' | 'pageBreak' | 'boneyard'

export interface FountainToken {
  kind: FountainKind
  /** Text without the Fountain marks (a dialogue or an action can have several lines, joined with \n) */
  text: string
  /** First source line (0-based) and how many source lines the element takes */
  line: number
  lines: number
  /** Section depth (# = 1) */
  depth?: number
  /** Scene number written as #12A# */
  number?: string
  /** Character cue: the name without extensions, and the extensions as written ("(V.O.)") */
  name?: string
  ext?: string
  /** Dual dialogue: the first speaker is 'left', the one marked with ^ 'right' (cue, parentheticals and dialogue) */
  dual?: 'left' | 'right'
  /** Written with a forcing mark (. ! @ >) */
  forced?: boolean
}

/** A title page field: its key as written ("Draft date") and its lines */
export interface TitleField { key: string; values: string[] }

export interface FountainDoc {
  title: TitleField[]
  tokens: FountainToken[]
}

/** Scene heading prefixes (case does not matter), followed by a dot or a space */
export const SCENE_RE = /^(INT\.?\/EXT|I\/E|INT|EXT|EST)[. ]/i
/** Scene number at the end of a heading: #1#, #12A#, #I-1-A# */
const SCENE_NUM_RE = /\s*#([^#\s][^#]*)#\s*$/
const TITLE_KEY_RE = /^([A-Za-z][A-Za-z ]*):\s*(.*)$/
/** A title page starts with one of the standard keys (so "CUT TO:" on the first line stays a transition); the keys
 *  after it can be any */
const TITLE_FIRST_RE = /^(title|credit|authors?|source|draft date|date|contact|notes|copyright|revision):/i

/** A line in capitals that can be a character cue: letters, all upper case (extensions and the dual mark ignored) */
export function isCapsName(line: string): boolean {
  const name = line.replace(/\^\s*$/, '').replace(/\([^)]*\)?/g, '').trim()
  return /\p{L}/u.test(name) && name === name.toUpperCase()
}

/** Splits a cue into its name and extensions: "JOHN (V.O.) ^" → { name: 'JOHN', ext: '(V.O.)', dual: true } */
export function splitCue(cue: string): { name: string; ext: string; dual: boolean } {
  let s = cue.trim().replace(/^@/, '')
  const dual = /\^\s*$/.test(s)
  if (dual) s = s.replace(/\s*\^\s*$/, '')
  const i = s.indexOf('(')
  return i < 0 ? { name: s.trim(), ext: '', dual } : { name: s.slice(0, i).trim(), ext: s.slice(i).trim(), dual }
}

/** Removes the boneyard (/* … *\/) and keeps its line breaks, so the lines keep their numbers; the removed parts are
 *  returned with their first line */
function cutBoneyard(src: string): { text: string; bones: FountainToken[] } {
  const bones: FountainToken[] = []
  let text = '', i = 0
  for (;;) {
    const a = src.indexOf('/*', i)
    if (a < 0) { text += src.slice(i); break }
    const b = src.indexOf('*/', a + 2), end = b < 0 ? src.length : b + 2, part = src.slice(a, end)
    text += src.slice(i, a)
    const line = text.split('\n').length - 1
    bones.push({ kind: 'boneyard', text: part.slice(2, b < 0 ? undefined : -2), line, lines: part.split('\n').length })
    text += part.replace(/[^\n]/g, '')
    i = end
  }
  return { text, bones }
}

/** Reads a Fountain script */
export function parseFountain(source: string): FountainDoc {
  const { text, bones } = cutBoneyard(String(source || '').replace(/\r\n?/g, '\n'))
  const L = text.split('\n'), title: TitleField[] = [], tokens: FountainToken[] = []
  let i = 0
  // Title page: "Key: value" lines at the very top, up to the first empty line; indented lines continue a value
  while (i < L.length && !L[i].trim()) i++
  if (i < L.length && TITLE_FIRST_RE.test(L[i])) {
    for (; i < L.length && L[i].trim(); i++) {
      const m = L[i].match(TITLE_KEY_RE)
      if (m && !/^(\s{3,}|\t)/.test(L[i])) title.push({ key: m[1].trim(), values: m[2].trim() ? [m[2].trim()] : [] })
      else if (title.length) title[title.length - 1].values.push(L[i].trim())
    }
  } else i = 0
  const blank = (k: number) => k >= L.length || !L[k].trim()
  const push = (tk: FountainToken) => { tokens.push(tk); return tk }
  let prevBlank = true, inDialogue = false, dual: 'right' | undefined
  for (; i < L.length; i++) {
    const raw = L[i], t = raw.trim()
    if (!t) {
      // Two spaces keep a dialogue going over an empty line
      const last = tokens[tokens.length - 1]
      if (inDialogue && raw === '  ' && last && last.kind === 'dialogue') { last.text += '\n'; last.lines++; continue }
      inDialogue = false; dual = undefined; prevBlank = true
      continue
    }
    const last = tokens[tokens.length - 1], m0 = (re: RegExp) => t.match(re)
    let m: RegExpMatchArray | null
    if (/^={3,}$/.test(t)) { push({ kind: 'pageBreak', text: '', line: i, lines: 1 }); inDialogue = false }
    else if ((m = m0(/^(#+)\s*(.*)$/))) { push({ kind: 'section', text: m[2], depth: m[1].length, line: i, lines: 1 }); inDialogue = false }
    else if ((m = m0(/^=(?!=)\s*(.*)$/))) { push({ kind: 'synopsis', text: m[1], line: i, lines: 1 }); inDialogue = false }
    else if (inDialogue) {
      const kind = /^\(.*\)$/.test(t) ? 'parenthetical' : 'dialogue'
      if (kind === 'dialogue' && last && last.kind === 'dialogue' && last.line + last.lines === i) { last.text += '\n' + t; last.lines++ }
      else push({ kind, text: t, line: i, lines: 1, dual })
    }
    else if ((m = m0(/^>\s*(.*?)\s*<$/))) push({ kind: 'centered', text: m[1], line: i, lines: 1 })
    else if ((m = m0(/^>\s*(.*)$/))) push({ kind: 'transition', text: m[1], line: i, lines: 1, forced: true })
    else if ((m = m0(/^~\s*(.*)$/))) push({ kind: 'lyrics', text: m[1], line: i, lines: 1 })
    else if (prevBlank && (m = m0(/^\.(?!\.)\s*(.+)$/))) push(sceneToken(m[1], i, true))
    else if (prevBlank && SCENE_RE.test(t)) push(sceneToken(t, i, false))
    else if (prevBlank && !blank(i + 1) && (t[0] === '@' || (!/^(!|\[\[)/.test(t) && isCapsName(t)))) {
      const cue = splitCue(t)
      dual = cue.dual ? 'right' : undefined
      push({ kind: 'character', text: t.replace(/^@/, '').replace(/\s*\^\s*$/, ''), name: cue.name, ext: cue.ext, line: i, lines: 1, forced: t[0] === '@', dual })
      if (dual) markLeft(tokens, tokens.length - 1)
      inDialogue = true
    }
    else if (prevBlank && blank(i + 1) && isCapsName(t) && /TO:$/.test(t)) push({ kind: 'transition', text: t, line: i, lines: 1 })
    else {
      const forced = t[0] === '!', body = forced ? raw.replace(/^\s*!/, '') : raw.replace(/\s+$/, '')
      if (!prevBlank && last && last.kind === 'action' && last.line + last.lines === i) { last.text += '\n' + body; last.lines++ }
      else push({ kind: 'action', text: body, line: i, lines: 1, forced: forced || undefined })
    }
    prevBlank = false
  }
  // An action made only of a note is a note; the boneyard goes back in its place (it is drawn nowhere)
  tokens.forEach(tk => { if (tk.kind === 'action' && /^\s*\[\[[\s\S]*\]\]\s*$/.test(tk.text) && !/\]\][\s\S]*\[\[/.test(tk.text)) { tk.kind = 'note'; tk.text = tk.text.trim().slice(2, -2).trim() } })
  for (const b of bones) { const k = tokens.findIndex(x => x.line > b.line); tokens.splice(k < 0 ? tokens.length : k, 0, b) }
  return { title, tokens }
}

function sceneToken(text: string, line: number, forced: boolean): FountainToken {
  const m = text.match(SCENE_NUM_RE)
  return { kind: 'scene', text: (m ? text.slice(0, m.index) : text).trim(), number: m ? m[1] : undefined, line, lines: 1, forced: forced || undefined }
}
/** Dual dialogue: the speech before the one marked with ^ becomes the left one */
function markLeft(tokens: FountainToken[], right: number): void {
  let k = right - 1
  while (k >= 0 && (tokens[k].kind === 'dialogue' || tokens[k].kind === 'parenthetical')) k--
  if (k < 0 || tokens[k].kind !== 'character') return
  for (let j = k; j < right; j++) tokens[j].dual = 'left'
}

/** The tab's HTML for a Fountain text: one escaped code block (storage: src/features/editor/fountainHtml.ts) */
export const fountainTabHtml = (src: string): string => src ? '<pre><code>' + esc(src) + '</code></pre>' : ''

// ===== Text inside an element =====

/** Removes notes ([[ ]]), the emphasis marks and the escapes: the text as printed */
export function plainFountain(text: string): string {
  const kept: string[] = []
  return text.replace(/\\([*_\\[\]])/g, (_m, c: string) => { kept.push(c); return '' + (kept.length - 1) + '' })
    .replace(/\[\[[\s\S]*?\]\]/g, '').replace(/\*{1,3}|_/g, '').replace(/[ \t]+$/gm, '')
    .replace(/(\d+)/g, (_m, k: string) => kept[+k])
}

/** One element's text as HTML: escaped, *italic* **bold** ***bold italic*** _underline_, \ escapes, line breaks.
 *  Notes: 'show' = drawn as `span.fn-note`, 'hide' = left out, 'keep' = left as written (the visual page) */
export function inlineFountain(text: string, notes: 'show' | 'hide' | 'keep'): string {
  const kept: string[] = [], noteHtml: string[] = []
  let s = text.replace(/\\([*_\\[\]])/g, (_m, c: string) => { kept.push(c); return '' + (kept.length - 1) + '' })
  if (notes !== 'keep') s = s.replace(/\[\[([\s\S]*?)\]\]/g, (_m, n: string) => { noteHtml.push(notes === 'show' ? `<span class="fn-note">${esc(n.trim())}</span>` : ''); return '' + (noteHtml.length - 1) + '' })
  s = esc(s)
    .replace(/\*\*\*([^\n*]+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*([^\n*]+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^\n*]+?)\*/g, '<em>$1</em>')
    .replace(/_([^\n_]+?)_/g, '<u>$1</u>')
    .replace(/\n/g, '<br>')
  return s.replace(/(\d+)/g, (_m, k: string) => noteHtml[+k]).replace(/(\d+)/g, (_m, k: string) => esc(kept[+k]))
}

// ===== What the panels list =====

/** A scene or a section of the navigator */
export interface FountainScene { kind: 'scene' | 'section'; text: string; line: number; depth?: number; number: string }
/** Scenes (numbered in order, unless the heading has its own number: the next ones count on from it) and sections */
export function fountainScenes(doc: FountainDoc): FountainScene[] {
  let n = 0
  const num = (own: string | undefined) => { if (!own) return String(++n); const m = own.match(/^\d+/); if (m) n = +m[0]; return own }
  return doc.tokens.filter(tk => tk.kind === 'scene' || tk.kind === 'section').map(tk => tk.kind === 'scene'
    ? { kind: 'scene', text: plainFountain(tk.text), line: tk.line, number: num(tk.number) }
    : { kind: 'section', text: plainFountain(tk.text), line: tk.line, depth: tk.depth, number: '' })
}
/** Speaking characters with how many speeches each has, the most speaking first */
export function fountainCharacters(doc: FountainDoc): { name: string; speeches: number }[] {
  const count = new Map<string, number>()
  doc.tokens.forEach(tk => { if (tk.kind === 'character' && tk.name) { const n = tk.name.toUpperCase(); count.set(n, (count.get(n) || 0) + 1) } })
  return [...count].map(([name, speeches]) => ({ name, speeches })).sort((a, b) => b.speeches - a.speeches || a.name.localeCompare(b.name))
}
/** A scene heading split: prefix (INT.), place, time of day ("INT. KITCHEN - NIGHT" → INT., KITCHEN, NIGHT) */
export function splitHeading(h: string): { prefix: string; place: string; time: string } {
  const m = h.trim().match(/^(INT\.?\/EXT\.?|I\/E\.?|INT\.?|EXT\.?|EST\.?)\s*(.*)$/i)
  const rest = m ? m[2] : h.trim(), k = rest.lastIndexOf(' - ')
  return { prefix: m ? m[1].toUpperCase() : '', place: (k < 0 ? rest : rest.slice(0, k)).trim(), time: k < 0 ? '' : rest.slice(k + 3).trim() }
}
/** Places already used in the scene headings (for the suggestions), in order of first use */
export const fountainPlaces = (doc: FountainDoc): string[] =>
  [...new Set(doc.tokens.filter(tk => tk.kind === 'scene').map(tk => splitHeading(tk.text).place.toUpperCase()).filter(Boolean))]

// ===== Pages =====
// The standard page: US Letter, Courier 12 pt (10 characters an inch, 6 lines an inch), margins 1.5 in on the left and
// 1 in on the other sides; the text is 6 in (60 characters) wide, and a page holds about 55 lines.

/** Lines of text on a page */
export const PAGE_LINES = 55
/** Width in characters of each kind (dialogue 3.5 in, parenthetical 2.5 in, character cue from 3.7 in) */
const WIDTH: Partial<Record<FountainKind, number>> = { scene: 60, action: 60, character: 38, parenthetical: 25, dialogue: 35, lyrics: 35, transition: 60, centered: 60 }
/** Empty lines before each kind */
const SPACE: Partial<Record<FountainKind, number>> = { scene: 1, action: 1, character: 1, transition: 1, centered: 1, lyrics: 0 }
/** Printed kinds (sections, synopses, notes and the boneyard are not printed) */
export const PRINTED: ReadonlySet<FountainKind> = new Set(['scene', 'action', 'character', 'parenthetical', 'dialogue', 'lyrics', 'transition', 'centered'])

/** Lines a text takes at a width (in characters), wrapped at spaces like the page does; long words are cut */
export function wrapCount(text: string, width: number): number {
  let n = 0
  for (const line of plainFountain(text).split('\n')) {
    let cur = 0
    n++
    for (const w0 of line.split(/\s+/).filter(Boolean)) {
      let w = w0.length
      if (cur && cur + 1 + w <= width) { cur += 1 + w; continue }
      if (cur) n++
      while (w > width) { w -= width; n++ }
      cur = w
    }
  }
  return n
}
/** Lines an element takes on the page, without the empty lines before it */
export const tokenLines = (tk: FountainToken): number => WIDTH[tk.kind] ? wrapCount(tk.text, tk.dual ? Math.min(WIDTH[tk.kind]!, 28) : WIDTH[tk.kind]!) : 0

/** A block that stays on one page: a speech (cue, parentheticals, dialogue; both sides of a dual dialogue), or a single
 *  element; a scene heading stays with the block after it */
interface Block { tokens: FountainToken[]; lines: number; space: number }
function blocksOf(tokens: FountainToken[]): (Block | 'break')[] {
  const out: (Block | 'break')[] = []
  for (let i = 0; i < tokens.length; i++) {
    const tk = tokens[i]
    if (tk.kind === 'pageBreak') { out.push('break'); continue }
    if (tk.kind === 'boneyard') continue
    if (!PRINTED.has(tk.kind)) { out.push({ tokens: [tk], lines: 0, space: 0 }); continue }   // sections, synopses, notes: on screen only
    if (tk.kind === 'character') {
      const side = (from: number): [FountainToken[], number] => {
        const list = [tokens[from]]
        let j = from + 1
        while (j < tokens.length && (tokens[j].kind === 'parenthetical' || tokens[j].kind === 'dialogue')) list.push(tokens[j++])
        return [list, j]
      }
      let [list, j] = side(i)
      let lines = list.reduce((a, x) => a + tokenLines(x), 0)
      if (tk.dual === 'left' && tokens[j] && tokens[j].kind === 'character' && tokens[j].dual === 'right') {
        const [right, j2] = side(j)
        lines = Math.max(lines, right.reduce((a, x) => a + tokenLines(x), 0))
        list = list.concat(right); j = j2
      }
      out.push({ tokens: list, lines, space: 1 })
      i = j - 1
      continue
    }
    out.push({ tokens: [tk], lines: tokenLines(tk), space: SPACE[tk.kind] ?? 0 })
  }
  return out
}

/** The script on pages: each page lists its elements (sections, synopses and notes too, which take no room), each element with the empty lines drawn
 *  before it (none at the top of a page); `lines` = the lines used, the last page as far as it goes. An element longer
 *  than the space left goes to the next page whole (never split: the count is an estimate, like every page count before
 *  the final layout) */
export interface PagedToken { token: FountainToken; space: number }
export interface Paged { pages: PagedToken[][]; lines: number }
export function paginate(doc: FountainDoc, perPage = PAGE_LINES): Paged {
  const pages: PagedToken[][] = [[]], use = [0], blocks = blocksOf(doc.tokens)
  const newPage = () => { if (pages[pages.length - 1].length) { pages.push([]); use.push(0) } }
  for (let b = 0; b < blocks.length; b++) {
    const bl = blocks[b]
    if (bl === 'break') { newPage(); continue }
    const next = blocks[b + 1], keep = bl.tokens[0].kind === 'scene' && next && next !== 'break' ? next.space + next.lines : 0
    const used = use[use.length - 1]
    let space = used ? bl.space : 0
    if (used && used + space + bl.lines + keep > perPage) { newPage(); space = 0 }
    bl.tokens.forEach((token, k) => pages[pages.length - 1].push({ token, space: k ? 0 : space }))
    use[use.length - 1] += space + bl.lines
  }
  if (!pages[pages.length - 1].length) { pages.pop(); use.pop() }
  return { pages, lines: pages.length ? (pages.length - 1) * perPage + use[use.length - 1] : 0 }
}
